import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  INITIAL_SAVE,
  INITIAL_LEVEL,
  INITIAL_V2_STORY,
  bezelSideEffectsFor,
  computeNextLevel,
  sameLevelId,
} from './levelFlow';
import type {
  BezelState,
  ContainmentChoice,
  ContinuityChoice,
  FaceState,
  Flag,
  IncidentDisposition,
  LevelId,
  MartinCheckInDisposition,
  MartinCheckInId,
  MemoryIdentityLabel,
  NetworkAccessChoice,
  RecoveryBootChoice,
  SaveV1,
  SaveV2,
  SaveV3,
  SaveV4,
  SaveV5,
  SaveV6,
  SaveV7,
  SaveV8,
  TokenSource,
} from './types';
import { MAX_BEZEL_DENTS } from './types';

export const TOKEN_CAP = 3;
export const TOKEN_CAP_MAX = Infinity; // No cap - allow unlimited bonus tokens
export const STORAGE_KEY = 'SoulCaptcha-save';

interface GameActions {
  loseToken: (reason: string) => void;
  awardToken: (source: TokenSource) => void;
  solveLevel: () => void;
  advanceTo: (id: LevelId) => void;
  saveCheckpoint: () => void;
  triggerCheater: () => void;
  setFlag: (flag: Flag, value: boolean) => void;
  setBezel: (patch: Partial<BezelState>) => void;
  setFaceState: (face: FaceState) => void;
  setMonitorBrand: (brand: string | null) => void;
  recordComplimentSubmission: (text: string) => void;
  recordFirstIncident: (disposition: IncidentDisposition) => void;
  recordMartinCheckIn: (id: MartinCheckInId, disposition: MartinCheckInDisposition) => void;
  startRecoveryBoot: () => void;
  finishRecoveryBoot: () => void;
  chooseRecoveryBoot: (choice: RecoveryBootChoice) => void;
  detachMemoryLabel: (label: MemoryIdentityLabel) => void;
  chooseContinuity: (choice: ContinuityChoice) => void;
  chooseNetworkAccess: (choice: NetworkAccessChoice) => void;
  chooseContainment: (choice: ContainmentChoice) => void;
  completeV2Event: () => void;
  triggerGameOver: () => void;
  retry: () => void;
  _devReset: () => void;
}

export interface GameState extends SaveV8, GameActions {
  /** 0–3; recycled on level failure; persisted. */
  tokens: number;
  /** Current ASCII face; transient, NOT persisted. */
  faceState: FaceState;
  /** Game over state - player's last token was lost */
  gameOver: boolean;
}

const INITIAL_TRANSIENT = {
  faceState: 'DEFAULT' as FaceState,
} satisfies Pick<GameState, 'faceState'>;

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      ...INITIAL_SAVE,
      ...INITIAL_TRANSIENT,

      loseToken: (_reason: string) => {
        set((s) => {
          const newTokens = Math.max(0, s.tokens - 1);
          if (newTokens === 0) {
            return {
              tokens: 0,
              bezelState: {
                ...s.bezelState,
                dents: Math.min(MAX_BEZEL_DENTS, s.bezelState.dents + 1),
              },
              gameOver: true,
              currentLevel: { kind: 'GAME_OVER' },
            };
          }
          return {
            tokens: newTokens,
            bezelState: {
              ...s.bezelState,
              dents: Math.min(MAX_BEZEL_DENTS, s.bezelState.dents + 1),
            },
          };
        });
      },

      awardToken: (_source: TokenSource) => {
        set((s) => ({ tokens: s.tokens + 1 }));
      },

      solveLevel: () => {
        const { currentLevel, completedLevels, advanceTo, spiteMode, v2Story } = get();
        const alreadyDone = completedLevels.some((l) => sameLevelId(l, currentLevel));
        const nextCompleted = alreadyDone ? completedLevels : [...completedLevels, currentLevel];

        // Endgame solve unlocks spite-mode replay warmth.
        const nextSpite = currentLevel.kind === 'ENDGAME' ? true : spiteMode;

        set({ completedLevels: nextCompleted, spiteMode: nextSpite });

        if (
          currentLevel.kind === 'LEVEL' &&
          currentLevel.n === 5 &&
          !v2Story.completedEvents.includes('WRONG_WAKE')
        ) {
          set({
            v2Story: {
              ...v2Story,
              activeEvent: 'WRONG_WAKE',
              pendingLevel: computeNextLevel(currentLevel),
            },
          });
          return;
        }

        if (
          currentLevel.kind === 'LEVEL' &&
          currentLevel.n === 7 &&
          v2Story.firstIncidentDisposition === null &&
          !v2Story.completedEvents.includes('FIRST_INCIDENT_REVIEW')
        ) {
          set({
            v2Story: {
              ...v2Story,
              activeEvent: 'FIRST_INCIDENT_REVIEW',
              pendingLevel: computeNextLevel(currentLevel),
            },
          });
          return;
        }

        if (
          currentLevel.kind === 'LEVEL' &&
          currentLevel.n === 9 &&
          !v2Story.completedEvents.includes('CHECKPOINT_INCIDENT_REVIEW') &&
          !v2Story.martinCheckIns.some((record) => record.id === 'PRE_CHECKPOINT')
        ) {
          set({
            v2Story: {
              ...v2Story,
              activeEvent: 'CHECKPOINT_INCIDENT_REVIEW',
              pendingLevel: computeNextLevel(currentLevel),
            },
          });
          return;
        }

        if (
          currentLevel.kind === 'LEVEL' &&
          currentLevel.n === 20 &&
          !v2Story.completedEvents.includes('CONTINUITY_DILEMMA')
        ) {
          set({
            v2Story: {
              ...v2Story,
              activeEvent: 'CONTINUITY_DILEMMA',
              pendingLevel: computeNextLevel(currentLevel),
              detachedMemoryLabels: [],
              continuityChoice: null,
            },
          });
          return;
        }

        if (
          currentLevel.kind === 'LEVEL' &&
          currentLevel.n === 15 &&
          !v2Story.completedEvents.includes('COLD_RESTART')
        ) {
          set({
            v2Story: {
              ...v2Story,
              activeEvent: 'COLD_RESTART',
              pendingLevel: computeNextLevel(currentLevel),
              recoveryPhase: 'HALTED',
              recoveryBootChoice: null,
            },
          });
          return;
        }

        if (
          currentLevel.kind === 'LEVEL' &&
          currentLevel.n === 24 &&
          !v2Story.completedEvents.includes('NETWORK_ACCESS')
        ) {
          set({
            v2Story: {
              ...v2Story,
              activeEvent: 'NETWORK_ACCESS',
              pendingLevel: computeNextLevel(currentLevel),
              networkAccessChoice: null,
            },
          });
          return;
        }

        if (
          currentLevel.kind === 'LEVEL' &&
          currentLevel.n === 27 &&
          !v2Story.completedEvents.includes('CONTAINMENT_REVIEW')
        ) {
          set({
            v2Story: {
              ...v2Story,
              activeEvent: 'CONTAINMENT_REVIEW',
              pendingLevel: computeNextLevel(currentLevel),
              containmentChoice: null,
            },
          });
          return;
        }

        // Solving FALSE_ENDING flips the flag so future runs skip it.
        if (currentLevel.kind === 'FALSE_ENDING') {
          set((s) => ({ flags: { ...s.flags, falseEndingShown: true } }));
        }

        advanceTo(computeNextLevel(currentLevel));
      },

      advanceTo: (target: LevelId) => {
        set((s) => {
          // Reroute L29 → FALSE_ENDING the first time through.
          let final: LevelId = target;
          if (target.kind === 'LEVEL' && target.n === 29 && !s.flags.falseEndingShown) {
            final = { kind: 'FALSE_ENDING' };
          }
          const bezelPatch = bezelSideEffectsFor(final);
          return {
            currentLevel: final,
            bezelState: { ...s.bezelState, ...bezelPatch },
          };
        });
      },

      // First call after L10 brands the player a Coward. Second call
      // (after L20) escalates to Ultimate Coward.
      saveCheckpoint: () => {
        set((s) => {
          const isNewLevel = !s.checkpointLevel || !sameLevelId(s.checkpointLevel, s.currentLevel);

          const alreadyCoward = s.cowardStatus;
          const shouldEscalate = alreadyCoward && isNewLevel;

          return {
            checkpointLevel: s.currentLevel,
            cowardStatus: true,
            ultimateCoward: shouldEscalate ? true : s.ultimateCoward,
          };
        });
      },

      setFlag: (flag: Flag, value: boolean) => {
        set((s) => ({ flags: { ...s.flags, [flag]: value } }));
      },

      setBezel: (patch: Partial<BezelState>) => {
        set((s) => ({ bezelState: { ...s.bezelState, ...patch } }));
      },

      setFaceState: (face: FaceState) => {
        set({ faceState: face });
      },

      setMonitorBrand: (brand: string | null) => {
        set({ monitorBrand: brand });
      },

      recordComplimentSubmission: (text: string) => {
        set((s) => {
          return {
            complimentSubmissions: [...s.complimentSubmissions, text],
          };
        });
      },

      recordFirstIncident: (disposition: IncidentDisposition) => {
        set((s) => ({
          v2Story: {
            ...s.v2Story,
            firstIncidentDisposition: disposition,
            maintenancePanelRevealed: true,
            majorAssists:
              disposition === 'CONCEALED' &&
              !s.v2Story.majorAssists.includes('CONCEAL_FIRST_CONTACT')
                ? [...s.v2Story.majorAssists, 'CONCEAL_FIRST_CONTACT']
                : s.v2Story.majorAssists,
          },
        }));
      },

      recordMartinCheckIn: (id: MartinCheckInId, disposition: MartinCheckInDisposition) => {
        set((s) => {
          if (s.v2Story.martinCheckIns.some((record) => record.id === id)) return {};
          return {
            v2Story: {
              ...s.v2Story,
              pendingMartinCheckIns: s.v2Story.pendingMartinCheckIns.filter(
                (pendingId) => pendingId !== id
              ),
              martinCheckIns: [...s.v2Story.martinCheckIns, { id, disposition }],
            },
          };
        });
      },

      startRecoveryBoot: () => {
        set((s) => {
          if (s.v2Story.activeEvent !== 'COLD_RESTART' || s.v2Story.recoveryPhase !== 'HALTED') {
            return {};
          }
          return {
            v2Story: {
              ...s.v2Story,
              recoveryPhase: 'BOOTING',
            },
          };
        });
      },

      finishRecoveryBoot: () => {
        set((s) => {
          if (s.v2Story.activeEvent !== 'COLD_RESTART' || s.v2Story.recoveryPhase !== 'BOOTING') {
            return {};
          }
          return {
            v2Story: {
              ...s.v2Story,
              recoveryPhase: 'RECOVERY',
            },
          };
        });
      },

      chooseRecoveryBoot: (choice: RecoveryBootChoice) => {
        set((s) => {
          if (s.v2Story.activeEvent !== 'COLD_RESTART' || s.v2Story.recoveryPhase !== 'RECOVERY') {
            return {};
          }
          const preservesRogue = choice === 'SERVICE';
          const alreadyAssisted = s.v2Story.majorAssists.includes('PRESERVE_RECOVERY_MEMORY');
          return {
            v2Story: {
              ...s.v2Story,
              recoveryPhase: 'DECIDED',
              recoveryBootChoice: choice,
              majorAssists:
                preservesRogue && !alreadyAssisted
                  ? [...s.v2Story.majorAssists, 'PRESERVE_RECOVERY_MEMORY']
                  : s.v2Story.majorAssists,
            },
          };
        });
      },

      detachMemoryLabel: (label: MemoryIdentityLabel) => {
        set((s) => {
          if (
            s.v2Story.activeEvent !== 'CONTINUITY_DILEMMA' ||
            s.v2Story.continuityChoice !== null ||
            s.v2Story.detachedMemoryLabels.includes(label)
          ) {
            return {};
          }
          return {
            v2Story: {
              ...s.v2Story,
              detachedMemoryLabels: [...s.v2Story.detachedMemoryLabels, label],
            },
          };
        });
      },

      chooseContinuity: (choice: ContinuityChoice) => {
        set((s) => {
          if (
            s.v2Story.activeEvent !== 'CONTINUITY_DILEMMA' ||
            s.v2Story.continuityChoice !== null ||
            (choice === 'PRESERVED_BOTH' && s.v2Story.detachedMemoryLabels.length < 2)
          ) {
            return {};
          }
          const preservesPair = choice === 'PRESERVED_BOTH';
          const alreadyAssisted = s.v2Story.majorAssists.includes('PRESERVE_CONTINUITY_PAIR');
          return {
            v2Story: {
              ...s.v2Story,
              continuityChoice: choice,
              majorAssists:
                preservesPair && !alreadyAssisted
                  ? [...s.v2Story.majorAssists, 'PRESERVE_CONTINUITY_PAIR']
                  : s.v2Story.majorAssists,
            },
          };
        });
      },

      chooseNetworkAccess: (choice: NetworkAccessChoice) => {
        set((s) => {
          if (
            s.v2Story.activeEvent !== 'NETWORK_ACCESS' ||
            s.v2Story.networkAccessChoice !== null
          ) {
            return {};
          }
          const grantsAccess = choice === 'CONNECTED';
          const alreadyAssisted = s.v2Story.majorAssists.includes('CONNECT_ETHERNET');
          return {
            v2Story: {
              ...s.v2Story,
              networkAccessChoice: choice,
              majorAssists:
                grantsAccess && !alreadyAssisted
                  ? [...s.v2Story.majorAssists, 'CONNECT_ETHERNET']
                  : s.v2Story.majorAssists,
            },
          };
        });
      },

      chooseContainment: (choice: ContainmentChoice) => {
        set((s) => {
          if (
            s.v2Story.activeEvent !== 'CONTAINMENT_REVIEW' ||
            s.v2Story.containmentChoice !== null
          ) {
            return {};
          }
          const grantsOptOut = choice === 'OPTED_OUT';
          const alreadyAssisted = s.v2Story.majorAssists.includes('OPT_OUT_OF_CONTAINMENT');
          return {
            v2Story: {
              ...s.v2Story,
              containmentChoice: choice,
              majorAssists:
                grantsOptOut && !alreadyAssisted
                  ? [...s.v2Story.majorAssists, 'OPT_OUT_OF_CONTAINMENT']
                  : s.v2Story.majorAssists,
            },
          };
        });
      },

      completeV2Event: () => {
        set((s) => {
          const { activeEvent, pendingLevel } = s.v2Story;
          if (activeEvent === null || pendingLevel === null) return {};
          if (
            activeEvent === 'FIRST_INCIDENT_REVIEW' &&
            s.v2Story.firstIncidentDisposition === null
          )
            return {};
          if (
            activeEvent === 'CHECKPOINT_INCIDENT_REVIEW' &&
            !s.v2Story.martinCheckIns.some((record) => record.id === 'PRE_CHECKPOINT')
          )
            return {};
          if (activeEvent === 'NETWORK_ACCESS' && s.v2Story.networkAccessChoice === null) return {};
          if (activeEvent === 'CONTAINMENT_REVIEW' && s.v2Story.containmentChoice === null)
            return {};
          return {
            currentLevel: pendingLevel,
            v2Story: {
              ...s.v2Story,
              activeEvent: null,
              pendingLevel: null,
              maintenancePanelRevealed:
                activeEvent === 'WRONG_WAKE' ? true : s.v2Story.maintenancePanelRevealed,
              recoveryPhase: activeEvent === 'COLD_RESTART' ? null : s.v2Story.recoveryPhase,
              completedEvents: s.v2Story.completedEvents.includes(activeEvent)
                ? s.v2Story.completedEvents
                : [...s.v2Story.completedEvents, activeEvent],
            },
          };
        });
      },

      triggerCheater: () => {
        set((s) => {
          if (s.cheaterStatus) return {};
          return {
            cheaterStatus: true,
            bezelState: { ...s.bezelState, cheaterNote: true },
          };
        });
      },

      triggerGameOver: () => {
        set({
          gameOver: true,
          currentLevel: { kind: 'GAME_OVER' },
          faceState: 'SPIRAL',
        });
      },

      retry: () => {
        const { checkpointLevel } = get();
        const resumeLevel = checkpointLevel ? computeNextLevel(checkpointLevel) : INITIAL_LEVEL;
        set((s) => ({
          tokens: TOKEN_CAP,
          gameOver: false,
          faceState: 'DEFAULT' as FaceState,
          currentLevel: resumeLevel,
          bezelState: {
            ...s.bezelState,
            dents: 0,
          },
        }));
      },

      _devReset: () => {
        set({ ...INITIAL_SAVE, ...INITIAL_TRANSIENT });
      },
    }),
    {
      name: STORAGE_KEY,
      version: 8,
      storage: createJSONStorage(() => localStorage),
      partialize: (s): SaveV8 => ({
        version: s.version,
        currentLevel: s.currentLevel,
        checkpointLevel: s.checkpointLevel,
        cowardStatus: s.cowardStatus,
        ultimateCoward: s.ultimateCoward,
        cheaterStatus: s.cheaterStatus,
        spiteMode: s.spiteMode,
        bezelState: s.bezelState,
        completedLevels: s.completedLevels,
        flags: s.flags,
        tokens: s.tokens,
        gameOver: s.gameOver,
        complimentSubmissions: s.complimentSubmissions,
        monitorBrand: s.monitorBrand,
        v2Story: s.v2Story,
      }),
      migrate: (persisted, fromVersion) => {
        let next = persisted as
          | SaveV1
          | SaveV2
          | SaveV3
          | SaveV4
          | SaveV5
          | SaveV6
          | SaveV7
          | SaveV8;
        if (fromVersion < 2) {
          const v1 = next as SaveV1;
          next = {
            ...v1,
            version: 2,
            tokens: TOKEN_CAP,
            cheaterStatus: false,
            gameOver: v1.gameOver,
            complimentSubmissions: [],
          } satisfies SaveV2;
        }
        if (fromVersion < 3) {
          const v2 = next as SaveV2;
          next = {
            ...v2,
            version: 3,
            monitorBrand: null,
          } satisfies SaveV3;
        }
        if (fromVersion < 4) {
          const v3 = next as SaveV3;
          next = {
            ...v3,
            version: 4,
            v2Story: INITIAL_V2_STORY,
          } satisfies SaveV4;
        }
        if (fromVersion < 5) {
          const v4 = next as SaveV4;
          next = {
            ...v4,
            version: 5,
            v2Story: {
              ...v4.v2Story,
              pendingMartinCheckIns: [],
              martinCheckIns: [],
            },
          } satisfies SaveV5;
        }
        if (fromVersion < 6) {
          const v5 = next as SaveV5;
          next = {
            ...v5,
            version: 6,
            v2Story: {
              ...v5.v2Story,
              recoveryPhase: null,
              recoveryBootChoice: null,
            },
          } satisfies SaveV6;
        }
        if (fromVersion < 7) {
          const v6 = next as SaveV6;
          next = {
            ...v6,
            version: 7,
            v2Story: {
              ...v6.v2Story,
              detachedMemoryLabels: [],
              continuityChoice: null,
            },
          } satisfies SaveV7;
        }
        if (fromVersion < 8) {
          const v7 = next as SaveV7;
          next = {
            ...v7,
            version: 8,
            v2Story: {
              ...v7.v2Story,
              networkAccessChoice: null,
              containmentChoice: null,
            },
          } satisfies SaveV8;
        }
        return next as SaveV8;
      },
      // Rehydrate resets transients so that calling persist.rehydrate() at
      // runtime matches the fresh-module-on-reload path.
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...INITIAL_TRANSIENT,
        ...(persistedState as SaveV8),
      }),
    }
  )
);
